import React, { useEffect, useState, useRef } from "react";
import { http } from "@/context/AuthContext";
import { Loader2, MessageSquare, Bot, Mail, Send, ChevronLeft, AlertCircle } from "lucide-react";

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function StatusPill({ status }) {
  const map = {
    active: "bg-green-100 text-green-800",
    escalated: "bg-gold/20 text-navy",
    closed: "bg-navy/10 text-navy/60",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${map[status] || "bg-navy/10 text-navy/60"}`}>
      {status || "active"}
    </span>
  );
}

// =========================================================
// AI Chat Sessions Tab
// =========================================================
function AiChatsTab() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("all");

  async function load() {
    setLoading(true);
    const params = filter === "all" ? "" : `?status=${filter}`;
    try {
      const r = await http.get(`/admin/chat-sessions${params}`);
      setSessions(r.data?.sessions || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [filter]);

  async function openSession(s) {
    setSelected(s);
    setDetail(null);
    try {
      const r = await http.get(`/admin/chat-sessions/${s.session_id}`);
      setDetail(r.data);
    } catch {
      setDetail({ session: s, messages: [] });
    }
  }

  if (selected) {
    return (
      <div data-testid="admin-ai-chat-detail" className="card p-0">
        <div className="p-4 border-b border-navy/5 flex items-center gap-3">
          <button
            onClick={() => {
              setSelected(null);
              setDetail(null);
            }}
            className="p-1.5 hover:bg-navy/5 rounded-lg"
            data-testid="ai-chat-back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="font-medium text-navy">
              {selected.visitor_name || selected.visitor_email || "Anonymous visitor"}
            </div>
            <div className="text-xs text-navy/55 flex items-center gap-2">
              <StatusPill status={selected.status} />
              <span>{selected.message_count || 0} messages</span>
              <span>·</span>
              <span>{formatTime(selected.last_active_at)}</span>
            </div>
          </div>
          {selected.visitor_email && (
            <a
              href={`mailto:${selected.visitor_email}`}
              className="btn-secondary text-xs gap-1.5"
              data-testid="ai-chat-email-link"
            >
              <Mail className="w-3 h-3" /> Reply
            </a>
          )}
        </div>
        <div className="p-4 max-h-[600px] overflow-y-auto space-y-3 bg-soft/40">
          {detail === null ? (
            <div className="flex items-center gap-2 text-navy/60">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : detail.messages.length === 0 ? (
            <p className="text-navy/55 text-sm">No messages in this session.</p>
          ) : (
            detail.messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-white border border-navy/10 text-navy"
                      : "bg-navy text-soft"
                  }`}
                >
                  <div
                    className={`text-[10px] uppercase tracking-widest mb-1 ${
                      m.role === "user" ? "text-navy/40" : "text-soft/50"
                    }`}
                  >
                    {m.role === "user" ? "Visitor" : "AI"} · {formatTime(m.created_at)}
                  </div>
                  {m.content}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-ai-chats">
      <div className="flex items-center gap-2 mb-4">
        {["all", "escalated", "active"].map((f) => (
          <button
            key={f}
            data-testid={`ai-chat-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest transition-colors ${
              filter === f
                ? "bg-navy text-soft"
                : "bg-white border border-navy/10 text-navy hover:bg-navy/5"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-navy/60">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : sessions.length === 0 ? (
        <div className="card text-center py-10 text-navy/55">
          <Bot className="w-10 h-10 text-gold mx-auto mb-3" />
          <p>No AI chat sessions yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map((s) => (
            <button
              key={s.session_id}
              data-testid={`ai-chat-row-${s.session_id}`}
              onClick={() => openSession(s)}
              className="w-full text-left card hover:border-gold/40 transition-colors p-4 flex items-start gap-3"
            >
              <Bot className="w-5 h-5 text-gold mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-navy text-sm">
                    {s.visitor_name || s.visitor_email || "Anonymous"}
                  </span>
                  <StatusPill status={s.status} />
                  {s.status === "escalated" && (
                    <AlertCircle className="w-3.5 h-3.5 text-gold" />
                  )}
                </div>
                <div className="text-xs text-navy/55 mt-1">
                  {s.message_count || 0} messages · {formatTime(s.last_active_at)}
                </div>
                {s.escalation_notes && (
                  <div className="text-xs text-navy/70 mt-1 italic line-clamp-1">
                    &quot;{s.escalation_notes}&quot;
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =========================================================
// Client Conversations Tab
// =========================================================
function ConversationsTab() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  async function loadThreads() {
    setLoading(true);
    try {
      const r = await http.get("/admin/conversations");
      setThreads(r.data?.threads || []);
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(userId) {
    setSelectedUserId(userId);
    setDetail(null);
    try {
      const r = await http.get(`/admin/conversations/${userId}`);
      setDetail(r.data);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {
      setDetail({ user: null, messages: [] });
    }
  }

  useEffect(() => {
    loadThreads();
  }, []);

  async function sendReply(e) {
    e.preventDefault();
    if (!reply.trim() || !selectedUserId) return;
    setSending(true);
    try {
      await http.post("/admin/conversations/reply", {
        user_id: selectedUserId,
        body: reply.trim(),
      });
      setReply("");
      await loadDetail(selectedUserId);
      await loadThreads();
    } finally {
      setSending(false);
    }
  }

  if (selectedUserId && detail) {
    return (
      <div data-testid="admin-conversation-detail" className="card p-0 flex flex-col h-[640px]">
        <div className="p-4 border-b border-navy/5 flex items-center gap-3">
          <button
            onClick={() => {
              setSelectedUserId(null);
              setDetail(null);
            }}
            className="p-1.5 hover:bg-navy/5 rounded-lg"
            data-testid="conv-back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="font-medium text-navy">
              {detail.user
                ? `${detail.user.first_name || ""} ${detail.user.last_name || ""}`.trim() ||
                  detail.user.email
                : "Client"}
            </div>
            <div className="text-xs text-navy/55">{detail.user?.email}</div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-soft/40">
          {detail.messages.length === 0 ? (
            <p className="text-navy/55 text-sm">No messages yet.</p>
          ) : (
            detail.messages.map((m, i) => {
              const isClient = m.from === "client";
              return (
                <div key={i} className={`flex ${isClient ? "justify-start" : "justify-end"}`}>
                  <div
                    className={`max-w-md rounded-2xl px-4 py-3 text-sm ${
                      isClient
                        ? "bg-white border border-navy/10 text-navy"
                        : "bg-navy text-soft"
                    }`}
                  >
                    <div
                      className={`text-[10px] uppercase tracking-widest mb-1 ${
                        isClient ? "text-navy/45" : "text-soft/55"
                      }`}
                    >
                      {isClient ? m.from_name || "Client" : m.from_name || "ConnectHER Team"} ·{" "}
                      {formatTime(m.created_at)}
                    </div>
                    <div className="whitespace-pre-wrap leading-relaxed">{m.body}</div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={endRef} />
        </div>
        <form
          onSubmit={sendReply}
          className="border-t border-navy/5 p-4 flex gap-3"
          data-testid="conv-reply-form"
        >
          <input
            type="text"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Reply to client..."
            data-testid="conv-reply-input"
            className="flex-1 input-field"
          />
          <button
            type="submit"
            disabled={sending || !reply.trim()}
            data-testid="conv-reply-send"
            className="btn-primary"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4" /> Send
              </>
            )}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-conversations">
      {loading ? (
        <div className="flex items-center gap-2 text-navy/60">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : threads.length === 0 ? (
        <div className="card text-center py-10 text-navy/55">
          <MessageSquare className="w-10 h-10 text-gold mx-auto mb-3" />
          <p>No client conversations yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {threads.map((t) => (
            <button
              key={t.user_id}
              data-testid={`conv-row-${t.user_id}`}
              onClick={() => loadDetail(t.user_id)}
              className="w-full text-left card hover:border-gold/40 transition-colors p-4 flex items-start gap-3"
            >
              <MessageSquare className="w-5 h-5 text-navy/40 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-navy text-sm">{t.name}</span>
                  {t.unread_count > 0 && (
                    <span className="bg-gold text-navy text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {t.unread_count} new
                    </span>
                  )}
                </div>
                <div className="text-xs text-navy/55 mt-0.5">{t.email}</div>
                <div className="text-xs text-navy/70 mt-1 line-clamp-1">
                  <span className="text-navy/40">{t.last_from === "client" ? "→" : "←"}</span> {t.last_body}
                </div>
              </div>
              <div className="text-[11px] text-navy/45 shrink-0">{formatTime(t.last_at)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =========================================================
// Main page
// =========================================================
export default function AdminMessages() {
  const [tab, setTab] = useState("ai");

  return (
    <div data-testid="admin-messages-page">
      <h1 className="text-4xl">Messages</h1>
      <p className="mt-2 text-navy/65">
        AI chat sessions from the public site &amp; ongoing client portal conversations.
      </p>

      <div className="mt-6 mb-6 flex gap-2 border-b border-navy/10">
        <button
          data-testid="msg-tab-ai"
          onClick={() => setTab("ai")}
          className={`px-4 py-2 text-sm transition-colors ${
            tab === "ai"
              ? "text-navy border-b-2 border-gold -mb-px font-medium"
              : "text-navy/55 hover:text-navy"
          }`}
        >
          <Bot className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          AI Chat Sessions
        </button>
        <button
          data-testid="msg-tab-conv"
          onClick={() => setTab("conv")}
          className={`px-4 py-2 text-sm transition-colors ${
            tab === "conv"
              ? "text-navy border-b-2 border-gold -mb-px font-medium"
              : "text-navy/55 hover:text-navy"
          }`}
        >
          <MessageSquare className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Client Conversations
        </button>
      </div>

      {tab === "ai" ? <AiChatsTab /> : <ConversationsTab />}
    </div>
  );
}
